import { useEffect, useState } from 'react';

/**
 * SP-7c — hook generico per media query CSS-style. Riusabile in tutta la
 * dashboard per logica responsive component-level (es. sub-accordion solo
 * su mobile, hide Matrix view su xs, ecc.).
 *
 * SSR-safe: durante l'initial render server-side `window` non esiste —
 * ritorna `false` finché lo `useEffect` non si attiva client-side.
 *
 * @example
 *   const isMobile = useMediaQuery('(max-width: 767.98px)');
 *   const isCompact = useMediaQuery('(max-width: 575.98px)');
 */
export function useMediaQuery(query: string): boolean {
  const get = (): boolean =>
    typeof window !== 'undefined' && window.matchMedia(query).matches;

  const [matches, setMatches] = useState<boolean>(get);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    // Initial value (può essere cambiato tra il useState init e questa effect
    // se il viewport è cambiato — copertura difensiva).
    setMatches(mql.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/** Smartphone (xs + sm Bootstrap). */
export const useIsMobile = (): boolean => useMediaQuery('(max-width: 767.98px)');

/** Tablet portrait (md Bootstrap). */
export const useIsTablet = (): boolean =>
  useMediaQuery('(min-width: 768px) and (max-width: 991.98px)');

/** Desktop (lg+ Bootstrap). */
export const useIsDesktop = (): boolean => useMediaQuery('(min-width: 992px)');
